import React, { useState } from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import toast from 'react-hot-toast';
import { LogIn, Eye, EyeOff, Star } from 'lucide-react';
import { LoadingState } from '../../components/LoadingSpinner';

const LoginPage: React.FC = () => {
  usePageTitle('Login');
  const { login, isAuthenticated, isLoading: authLoading } = useAuth();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  if (isAuthenticated) {
    const from = location.state?.from?.pathname || '/admin';
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      toast.error('Todos os campos são obrigatórios');
      return;
    }

    setIsLoading(true);

    try {
      await login(formData.email, formData.password);
      toast.success('Login realizado com sucesso!');
    } catch (error: any) {
      toast.error('Erro ao fazer login, usuário ou senha inválidos.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center paper">
        <LoadingState message="Verificando credenciais..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen paper-dark flex flex-col relative overflow-hidden font-body">
      {/* Decorative sparkles in the background */}
      <Star className="sparkle w-8 h-8 text-mostarda" style={{ top: '8%', left: '10%' }} fill="currentColor" />
      <Star className="sparkle w-5 h-5 text-creme" style={{ top: '20%', right: '12%', animationDelay: '0.6s' }} fill="currentColor" />
      <Star className="sparkle w-10 h-10 text-mostarda" style={{ bottom: '12%', right: '8%', animationDelay: '1.1s' }} fill="currentColor" />
      <Star className="sparkle w-6 h-6 text-creme" style={{ bottom: '18%', left: '14%', animationDelay: '1.6s' }} fill="currentColor" />
      <Star className="sparkle w-4 h-4 text-mostarda" style={{ top: '50%', left: '5%', animationDelay: '0.3s' }} fill="currentColor" />
      <Star className="sparkle w-7 h-7 text-creme" style={{ top: '40%', right: '6%', animationDelay: '1.4s' }} fill="currentColor" />

      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-md w-full">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="h-12 flex items-center justify-center mb-6">
              <img src="/roda_de_premios_horizontal_white.svg" alt="Roda de Prêmios" className="h-full" />
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-black text-creme leading-tight">
              Bem-vindo de <span className="italic text-mostarda">volta</span>!
            </h1>
            <p className="mt-3 text-creme/85 italic">
              Faça login para gerenciar suas roletas
            </p>
          </div>

          {/* Card with form */}
          <div className="card-vintage rounded-2xl p-8">
            <form className="space-y-5" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="block text-sm font-bold text-tinta mb-1.5 uppercase tracking-wider">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="input-vintage block w-full px-4 py-3 rounded-lg text-base"
                  placeholder="seu@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-bold text-tinta mb-1.5 uppercase tracking-wider">
                  Senha
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    className="input-vintage block w-full px-4 py-3 pr-12 rounded-lg text-base"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-tinta-soft hover:text-bordo transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Esconder senha' : 'Mostrar senha'}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-end">
                <Link
                  to="/forgot-password"
                  className="text-sm font-bold text-bordo hover:text-bordo-dark underline italic"
                >
                  Esqueceu sua senha?
                </Link>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn-vintage w-full flex items-center justify-center py-3.5 px-4 rounded-lg text-base"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-creme"></div>
                ) : (
                  <>
                    <LogIn className="h-5 w-5 mr-2" />
                    Entrar
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Footer hint */}
          <div className="text-center mt-6 space-y-2">
            <p className="text-xs text-creme/70 uppercase tracking-widest font-bold">
              Área administrativa
            </p>
            <p className="text-sm text-creme/85">
              Não tem uma conta?{' '}
              <a
                href="https://pay.kiwify.com.br/fuvPJn1"
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold text-mostarda hover:text-mostarda-light underline"
              >
                Plano Mensal
              </a>
              {' '}ou{' '}
              <a
                href="https://pay.kiwify.com.br/Zyfotu1"
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold text-mostarda hover:text-mostarda-light underline"
              >
                Plano Anual
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
